import { DesignerSettings } from '@decisions/server/DecisionsFramework/Design';
import { DecisionsSettingInfo, PortalOptions } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal';
import {
  getDesignerSettings as fetchDesignerSettings,
  getDecisionsSettingInfo as fetchDecisionsSettingInfo,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal/PortalSettingsService';
import { GetState, SetState } from 'types/zustand';
import create from 'zustand';
import { registerForEventAndSubscribe } from 'utils/ClientEventUtils';

export const DESIGNER_SETTINGS_SAVED_KEY = 'DesignerSettings.Saved';

export function createSettingsState() {
  return {
    // these are possibly undefined as they are lazy-loaded.
    designerSettings: undefined as DesignerSettings | undefined,
    decisionsSettingInfo: undefined as DecisionsSettingInfo | undefined,
  };
}

export function createSettingsActions(get: GetState<SettingsStore>, set: SetState<SettingsStore>) {
  if (!DEV_SERVER && !IS_TEST) {
    registerForEventAndSubscribe(DESIGNER_SETTINGS_SAVED_KEY, 'settings-store', e => {
      set({ designerSettings: (e as any).detail as DesignerSettings });
    });
  }

  return {
    async getDecisionsSettings() {
      const { decisionsSettingInfo } = get();
      if (decisionsSettingInfo) {
        return decisionsSettingInfo;
      }

      try {
        const decisionsSettingInfo = await fetchDecisionsSettingInfo();
        set({ decisionsSettingInfo });
        return decisionsSettingInfo;
      } catch (error) {
        console.error('Unable to load Decisions Setting Info', error);
      }
    },
    async getDesignerSettings() {
      const { designerSettings } = get();
      if (designerSettings) {
        return designerSettings;
      }

      try {
        const designerSettings = await fetchDesignerSettings();
        set({ designerSettings });
        return designerSettings;
      } catch (error) {
        console.error('Unable to load Designer Settings', error);
      }
    },
    async initializeSettings({ isStudio }: PortalOptions) {
      if (isStudio) {
        get().actions.getDesignerSettings();
      }
      get().actions.getDecisionsSettings();
    },
  };
}

type SettingsStore = ReturnType<typeof createSettingsState> & { actions: ReturnType<typeof createSettingsActions> };

export const useSettingsStore = create<SettingsStore>((set, get) => {
  return {
    ...createSettingsState(),
    actions: createSettingsActions(get, set),
  };
});

export function getDesignerSettings(state: SettingsStore) {
  return state.designerSettings;
}

export function getDecisionsSettings(state: SettingsStore) {
  return state.decisionsSettingInfo;
}
